import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { useState } from 'react';

import { ContentEditable } from '../../editor/editor-ui/content-editable';
import { AutoLinkPlugin } from '../../editor/plugins/auto-link-plugin';
import { FloatingLinkEditorPlugin } from '../../editor/plugins/floating-link-editor-plugin';
import { FloatingTextFormatToolbarPlugin } from '../../editor/plugins/floating-text-format-plugin';
import { ImagesPlugin } from '../../editor/plugins/images-plugin';
import { LinkPlugin } from '../../editor/plugins/link-plugin';
import { ListMaxIndentLevelPlugin } from '../../editor/plugins/list-max-indent-level-plugin';
import { PasteImageUploadPlugin } from '../../editor/plugins/paste-image-upload-plugin';
import { FormatBulletedList } from '../../editor/plugins/toolbar/block-format/format-bulleted-list';
import { FormatHeading } from '../../editor/plugins/toolbar/block-format/format-heading';
import { FormatNumberedList } from '../../editor/plugins/toolbar/block-format/format-numbered-list';
import { FormatParagraph } from '../../editor/plugins/toolbar/block-format/format-paragraph';
import { FormatQuote } from '../../editor/plugins/toolbar/block-format/format-quote';
import { BlockFormatDropDown } from '../../editor/plugins/toolbar/block-format-toolbar-plugin';
import { ElementFormatToolbarPlugin } from '../../editor/plugins/toolbar/element-format-toolbar-plugin';
import { FontBackgroundToolbarPlugin } from '../../editor/plugins/toolbar/font-background-toolbar-plugin';
import { FontColorToolbarPlugin } from '../../editor/plugins/toolbar/font-color-toolbar-plugin';
import { FontFamilyToolbarPlugin } from '../../editor/plugins/toolbar/font-family-toolbar-plugin';
import { FontFormatToolbarPlugin } from '../../editor/plugins/toolbar/font-format-toolbar-plugin';
import { FontSizeToolbarPlugin } from '../../editor/plugins/toolbar/font-size-toolbar-plugin';
import { HistoryToolbarPlugin } from '../../editor/plugins/toolbar/history-toolbar-plugin';
import { ImageToolbarPlugin } from '../../editor/plugins/toolbar/image-toolbar-plugin';
import { LineHeightToolbarPlugin } from '../../editor/plugins/toolbar/line-height-toolbar-plugin';
import { LinkToolbarPlugin } from '../../editor/plugins/toolbar/link-toolbar-plugin';
import { ToolbarPlugin } from '../../editor/plugins/toolbar/toolbar-plugin';
import { VariableAutocompletePlugin } from '../../editor/plugins/variable-autocomplete-plugin';
import { Separator } from '../../ui/separator';

const placeholder = 'Type here...';

interface Variable {
  name: string;
  label: string;
}

export function Plugins({
  onImageUpload,
  variables,
  height,
  isInDialog,
  imageUploadEnabled = true,
}: {
  onImageUpload?: (file: File) => Promise<string>;
  variables?: Variable[];
  height?: string;
  isInDialog?: boolean;
  imageUploadEnabled?: boolean;
}) {
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    // `overflow-clip` (not `overflow-hidden`) clips the same way but does NOT
    // establish a scroll container, so the sticky toolbar below sticks to the
    // editor's scrollport (editor.tsx) instead of to this wrapper — keeping the
    // toolbar visible while the body scrolls.
    <div className="relative max-w-full overflow-clip">
      <ToolbarPlugin onImageUpload={onImageUpload}>
        {() => (
          <div className="vertical-align-middle sticky top-0 z-10 flex items-center gap-2 overflow-x-auto border-b bg-background p-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <HistoryToolbarPlugin />
              {imageUploadEnabled && <ImageToolbarPlugin />}
            </div>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <BlockFormatDropDown>
                <FormatParagraph />
                <FormatHeading levels={['h1', 'h2', 'h3']} />
                <FormatNumberedList />
                <FormatBulletedList />
                <FormatQuote />
              </BlockFormatDropDown>
              <ElementFormatToolbarPlugin separator={false} />
            </div>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <FontFamilyToolbarPlugin />
              <FontSizeToolbarPlugin />
              <LineHeightToolbarPlugin />
            </div>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <FontFormatToolbarPlugin />
              <FontColorToolbarPlugin />
              <FontBackgroundToolbarPlugin />
            </div>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <LinkToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} />
            </div>
          </div>
        )}
      </ToolbarPlugin>
      <div className="relative">
        <RichTextPlugin
          contentEditable={
            <div className="[&_p]:leading-normal text-[13px]">
              <div className="" ref={onRef}>
                <ContentEditable
                  placeholder={placeholder}
                  className={`ContentEditable__root relative block ${height || 'min-h-72'} overflow-auto px-8 py-4 focus:outline-none`}
                />
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />

        <ListPlugin />
        <ListMaxIndentLevelPlugin />

        <TabIndentationPlugin />

        <ClickableLinkPlugin />
        <AutoLinkPlugin />
        <LinkPlugin />

        <FloatingLinkEditorPlugin
          anchorElem={floatingAnchorElem}
          isLinkEditMode={isLinkEditMode}
          setIsLinkEditMode={setIsLinkEditMode}
        />

        <FloatingTextFormatToolbarPlugin
          anchorElem={floatingAnchorElem}
          setIsLinkEditMode={setIsLinkEditMode}
        />

        <ImagesPlugin />
        <PasteImageUploadPlugin onImageUpload={onImageUpload} />

        {variables && <VariableAutocompletePlugin variables={variables} isInDialog={isInDialog} />}
      </div>
    </div>
  );
}
